import { useEffect, useRef, useState, type ReactNode } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { SlidersHorizontal, ArrowRight, Minus, Plus, Check, Loader2, RotateCcw, MemoryStick, Users, RefreshCw } from 'lucide-react';
import { serverStore } from '../../stores/server.store';
import { userStore } from '../../stores/user.store';
import ToggleSwitch from '../common/ToggleSwitch';
import { CardLink } from './InfoGridCard';
import { cn } from '@/utils/cn';
import { can } from '@/utils/permissions';

export type QuickSaveResult = { ok: boolean; restartNeeded?: boolean };
type Status = 'idle' | 'saving' | 'saved';

const SAVE_DELAY_MS = 700;

const Stepper = ({ value, min, max, onChange, suffix }: { value: number; min: number; max: number; onChange: (v: number) => void; suffix?: string }) => (
  <div className="inline-flex items-center rounded-lg border border-line bg-black/25">
    <button
      type="button"
      aria-label="weniger"
      disabled={value <= min}
      onClick={() => onChange(Math.max(min, value - 1))}
      className="w-8 h-8 flex items-center justify-center text-fg-muted hover:text-fg disabled:opacity-35 disabled:cursor-not-allowed"
    >
      <Minus size={14} />
    </button>
    <span className="min-w-[62px] text-center font-mono text-sm text-fg tabular-nums">
      {value}{suffix && <span className="text-fg-muted"> {suffix}</span>}
    </span>
    <button
      type="button"
      aria-label="mehr"
      disabled={value >= max}
      onClick={() => onChange(Math.min(max, value + 1))}
      className="w-8 h-8 flex items-center justify-center text-fg-muted hover:text-fg disabled:opacity-35 disabled:cursor-not-allowed"
    >
      <Plus size={14} />
    </button>
  </div>
);

const Row = ({ icon, label, hint, status, children }: { icon: ReactNode; label: string; hint?: ReactNode; status?: Status; children: ReactNode }) => (
  <div className="flex items-center gap-4 px-5 py-3.5 border-b border-white/[0.05] last:border-b-0">
    <div className="w-8 h-8 rounded-lg bg-white/[0.05] flex items-center justify-center text-fg-muted shrink-0">{icon}</div>
    <div className="min-w-0 flex-1">
      <div className="text-sm text-fg flex items-center gap-2">
        {label}
        {status === 'saving' && <Loader2 size={12} className="animate-spin text-fg-muted" />}
        {status === 'saved' && <span className="text-online text-[11px] inline-flex items-center gap-1"><Check size={12} /> gespeichert</span>}
      </div>
      {hint && <div className="text-xs text-fg-muted mt-0.5">{hint}</div>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

interface Props {
  maxPlayers: number | null;
  onSaveMaxPlayers?: (n: number) => Promise<QuickSaveResult>;
  maxPlayersLimit?: number;
}

/**
 * Die Handvoll Einstellungen, die man ständig anfasst – direkt in der Übersicht und
 * ohne Speichern-Knopf: jede Änderung wird nach einem kurzen Moment selbst gesichert.
 */
export const QuickSettingsCard = observer(({ maxPlayers, onSaveMaxPlayers, maxPlayersLimit = 500 }: Props) => {
  const server = serverStore.activeServer;
  const navigate = useNavigate();
  const cu = userStore.currentUser;
  const canEdit = can(cu, 'can_configure_server');
  const canPower = can(cu, 'can_power_server');

  const [ram, setRam] = useState(server?.ram_limit ?? 4);
  const [players, setPlayers] = useState<number | null>(maxPlayers);
  const [autoUpdate, setAutoUpdate] = useState(!!server?.auto_update_config?.enabled);
  const [status, setStatus] = useState<Record<string, Status>>({});
  const [restartHint, setRestartHint] = useState(false);
  const [budget, setBudget] = useState<any>(null);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pending = useRef<Record<string, () => void>>({});

  // Werte vom Server übernehmen, solange hier keine eigene Änderung ansteht.
  useEffect(() => { if (!pending.current.ram) setRam(server?.ram_limit ?? 4); }, [server?.ram_limit]);
  useEffect(() => { if (!pending.current.players) setPlayers(maxPlayers); }, [maxPlayers]);
  useEffect(() => { setAutoUpdate(!!server?.auto_update_config?.enabled); }, [server?.auto_update_config?.enabled]);
  useEffect(() => {
    if (!server?.id) return;
    let alive = true;
    serverStore.fetchRamBudget(server.id).then((b) => { if (alive) setBudget(b); });
    return () => { alive = false; };
  }, [server?.id, server?.status]);
  // Wer weiterklickt, bevor gespeichert ist: sofort sichern statt verwerfen.
  useEffect(() => () => {
    Object.values(timers.current).forEach(clearTimeout);
    Object.values(pending.current).forEach((fire) => fire());
  }, []);

  if (!server || !canEdit) return null;
  const online = server.status === 'online';

  const mark = (key: string, s: Status) => {
    setStatus((prev) => ({ ...prev, [key]: s }));
    if (s === 'saved') setTimeout(() => setStatus((prev) => (prev[key] === 'saved' ? { ...prev, [key]: 'idle' } : prev)), 2000);
  };

  const schedule = (key: string, run: () => Promise<QuickSaveResult>) => {
    clearTimeout(timers.current[key]);
    const fire = async () => {
      delete pending.current[key];
      delete timers.current[key];
      mark(key, 'saving');
      const r = await run();
      mark(key, r.ok ? 'saved' : 'idle');
      if (r.ok && r.restartNeeded) setRestartHint(true);
    };
    pending.current[key] = fire;
    timers.current[key] = setTimeout(fire, SAVE_DELAY_MS);
  };

  const id = server.id;
  const changeRam = (v: number) => {
    setRam(v);
    schedule('ram', async () => ({
      ok: await serverStore.handleSaveServer({ id, ram_limit: v }, { silent: true }),
      restartNeeded: serverStore.servers.find((s) => s.id === id)?.status === 'online',
    }));
  };
  const changePlayers = (v: number) => {
    setPlayers(v);
    if (onSaveMaxPlayers) schedule('players', () => onSaveMaxPlayers(v));
  };
  const toggleAutoUpdate = async (v: boolean) => {
    setAutoUpdate(v);
    mark('au', 'saving');
    const au = { enabled: false, create_backup: true, restart: true, backup_target: 'hdd', ...(server.auto_update_config || {}) };
    const ok = await serverStore.handleSaveServer({ id, auto_update_config: { ...au, enabled: v } }, { silent: true });
    if (!ok) setAutoUpdate(!v);
    mark('au', ok ? 'saved' : 'idle');
  };

  const totalGb = budget?.totalGb ? Math.floor(budget.totalGb) : 32;
  // Was ein (Neu-)Start zusätzlich bräuchte vs. was der Host gerade frei hat.
  const extraNeeded = online ? ram - (server.ram_limit || 0) : ram;
  const tight = budget && extraNeeded > 0 && extraNeeded > budget.availableGb;
  const ramHint = budget
    ? tight
      ? <span className="text-warn">Knapp: auf dem Host sind nur noch {budget.availableGb} GB frei</span>
      : <>Frei auf dem Host: {budget.availableGb} von {budget.totalGb} GB</>
    : 'Arbeitsspeicher für diesen Server';

  return (
    <div className="bg-surface border border-line rounded-2xl overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-b border-line">
        <div className="flex items-center gap-2 text-sm font-medium text-fg">
          <SlidersHorizontal size={15} className="text-fg-muted" /> Schnelleinstellungen
          <span className="text-xs font-normal text-fg-muted hidden sm:inline">speichern sich selbst</span>
        </div>
        <CardLink onClick={() => navigate(`/server/${id}/settings`)}>Alle Einstellungen <ArrowRight size={13} /></CardLink>
      </div>

      <Row icon={<MemoryStick size={15} />} label="Arbeitsspeicher (RAM)" hint={ramHint} status={status.ram}>
        <Stepper value={ram} min={1} max={Math.max(ram, totalGb)} onChange={changeRam} suffix="GB" />
      </Row>

      {players !== null && onSaveMaxPlayers && (
        <Row icon={<Users size={15} />} label="Max. Spieler" hint="Wie viele gleichzeitig spielen dürfen" status={status.players}>
          <Stepper value={players} min={1} max={maxPlayersLimit} onChange={changePlayers} />
        </Row>
      )}

      <Row icon={<RefreshCw size={15} />} label="Automatische Updates" hint="Hält Spiel und Mods nachts aktuell" status={status.au}>
        <ToggleSwitch enabled={autoUpdate} onChange={toggleAutoUpdate} />
      </Row>

      {restartHint && online && (
        <div className={cn('flex items-center gap-3 mx-4 mb-4 rounded-lg px-3 py-2 text-xs bg-warn/10 border border-warn/25 text-warn')}>
          <RotateCcw size={14} className="shrink-0" />
          <span className="flex-1">Gespeichert – wirkt ab dem nächsten Neustart.</span>
          {canPower && (
            <button
              onClick={() => { setRestartHint(false); serverStore.powerServer(id, 'restarting'); }}
              className="font-medium text-fg hover:text-ember transition-colors shrink-0"
            >
              Jetzt neu starten
            </button>
          )}
        </div>
      )}
    </div>
  );
});
